import { useState } from 'react'
import styled from 'styled-components'
import { api } from '../../../services/api'

/**
 * "Nombres similares detectados" in KPIs por Categoría → Por Cliente.
 *
 * Each proposed group lists its names with the opportunities each one has and
 * why they were grouped. An admin picks the name that stays, can leave a name
 * out of the merge (it is then remembered as a different company), or mark the
 * whole group as different companies. The server remembers both decisions: a
 * merged spelling is written as the chosen one by the SharePoint import, and a
 * dismissed pair is not proposed again.
 */

export interface SimilarClientGroup {
    names: string[]
    suggested: string
    counts?: Record<string, number>
    reasons?: string[]
}

const Title = styled.div`
  font-size: 0.75rem;
  color: #F57C00;
  font-weight: 600;
  margin-bottom: 6px;
`

const Group = styled.div`
  background: #FFF8E1;
  border: 1px solid #FFE082;
  border-radius: 8px;
  padding: 8px;
  margin-bottom: 8px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  font-size: 0.75rem;
  color: #F57C00;
  font-weight: 600;
`

const Actions = styled.div`
  display: flex;
  gap: 4px;
`

const Name = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  padding: 2px 0;
`

const Count = styled.span`
  color: #888;
  font-size: 0.7rem;
`

const Stays = styled.span`
  background: #E8F5E9;
  color: #2E7D32;
  border-radius: 999px;
  padding: 0 6px;
  font-size: 0.65rem;
  font-weight: 600;
`

const Reasons = styled.div`
  margin-top: 4px;
  color: #8D6E63;
  font-size: 0.7rem;
`

const Button = styled.button<{ $variant?: 'merge' | 'dismiss' }>`
  background: ${({ $variant }) => ($variant === 'dismiss' ? 'white' : '#FF9800')};
  color: ${({ $variant }) => ($variant === 'dismiss' ? '#8D6E63' : 'white')};
  border: 1px solid ${({ $variant }) => ($variant === 'dismiss' ? '#D7CCC8' : '#FF9800')};
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const ErrorNote = styled.div`
  color: #C62828;
  font-size: 0.75rem;
  margin-bottom: 6px;
`

const groupId = (group: SimilarClientGroup) => group.names.join('|')

interface Props {
    groups: SimilarClientGroup[]
    /** Only admins merge or dismiss (the server checks it too); others do not see the box. */
    isAdmin: boolean
    /** Reload the KPIs and the proposals after a merge or a dismissal. */
    onChanged: () => void
}

export const SimilarClientsBox = ({ groups, isAdmin, onChanged }: Props) => {
    const [chosen, setChosen] = useState<Record<string, string>>({})
    const [leftOut, setLeftOut] = useState<Record<string, string[]>>({})
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)

    if (!isAdmin || groups.length === 0) return null

    const run = async (action: () => Promise<unknown>, failure: string) => {
        setBusy(true)
        setError(null)
        try {
            await action()
            onChanged()
        } catch (e: any) {
            setError(e?.response?.data?.detail || failure)
        } finally {
            setBusy(false)
        }
    }

    return (
        <div style={{ marginBottom: '12px' }}>
            <Title>⚠️ Nombres similares detectados</Title>
            {error && <ErrorNote role="alert">{error}</ErrorNote>}
            {groups.map((group) => {
                const id = groupId(group)
                const excluded = leftOut[id] || []
                const included = group.names.filter((n) => !excluded.includes(n))
                const stays = included.includes(chosen[id])
                    ? chosen[id]
                    : included.includes(group.suggested) ? group.suggested : included[0]
                const merged = included.filter((n) => n !== stays)
                const renamed = merged.reduce((sum, n) => sum + (group.counts?.[n] ?? 0), 0)

                const merge = () => {
                    const message = `¿Unificar ${merged.map((n) => `«${n}»`).join(', ')} en «${stays}»?`
                        + (group.counts ? ` Se renombrarán ${renamed} oportunidades.` : '')
                        + (excluded.length ? ` ${excluded.map((n) => `«${n}»`).join(', ')} quedará como otra empresa.` : '')
                    if (!window.confirm(message)) return
                    run(() => api.opportunities.mergeClients(stays, merged, excluded), 'Error al unificar clientes')
                }
                const dismiss = () => {
                    if (!window.confirm('¿Marcar estos nombres como empresas distintas? No se volverán a proponer juntos.')) return
                    run(() => api.opportunities.dismissSimilarClients(group.names), 'Error al descartar la propuesta')
                }

                return (
                    <Group key={id}>
                        <Header>
                            <span>Posible duplicado</span>
                            <Actions>
                                <Button type="button" $variant="merge" disabled={busy || merged.length === 0} onClick={merge}>
                                    Unificar
                                </Button>
                                <Button type="button" $variant="dismiss" disabled={busy} onClick={dismiss}>
                                    No son el mismo
                                </Button>
                            </Actions>
                        </Header>
                        {group.names.map((name) => {
                            const isIn = !excluded.includes(name)
                            return (
                                <Name key={name}>
                                    <input
                                        type="checkbox"
                                        checked={isIn}
                                        onChange={() => setLeftOut((prev) => ({
                                            ...prev,
                                            [id]: isIn ? [...excluded, name] : excluded.filter((n) => n !== name),
                                        }))}
                                        aria-label={`Incluir «${name}»`}
                                        title="Desmárcalo si no es la misma empresa"
                                    />
                                    <input
                                        type="radio"
                                        name={`merge-${id}`}
                                        checked={stays === name}
                                        disabled={!isIn}
                                        onChange={() => setChosen((prev) => ({ ...prev, [id]: name }))}
                                        aria-label={`Unificar en «${name}»`}
                                        title="El nombre que se queda"
                                    />
                                    <span style={{ textDecoration: isIn ? 'none' : 'line-through', opacity: isIn ? 1 : 0.6 }}>{name}</span>
                                    {group.counts && <Count>({group.counts[name] ?? 0} opp.)</Count>}
                                    {stays === name && <Stays>se queda</Stays>}
                                </Name>
                            )
                        })}
                        {group.reasons && group.reasons.length > 0 && <Reasons>{group.reasons.join(' · ')}</Reasons>}
                    </Group>
                )
            })}
        </div>
    )
}

export default SimilarClientsBox
